import {TrustAccountMemberRole} from '@sollapay/enums';
import {Typography} from '@sollapay/ui';
import {SegmentedControl} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import type {FC, ReactNode} from 'react';

const {OPERATOR, VIEWER} = TrustAccountMemberRole;

export interface ShareRolePickerProps {
  role: TrustAccountMemberRole;
  isOperatorForced?: boolean;
  onRoleChange: (role: TrustAccountMemberRole) => void;
  hasGuests: boolean;
  children?: ReactNode;
}

/** The role firm people join with, and under its hint what the role needs (`children`, e.g. the power of attorney). */
export const ShareRolePicker: FC<ShareRolePickerProps> = ({
  role,
  isOperatorForced = false,
  onRoleChange,
  hasGuests,
  children
}) => {
  const {t} = useTranslation('trustAccounts');
  const label = t('share.add.roleLabel');

  return (
    <div
      data-slot="share-role-picker"
      className="flex flex-col gap-2 rounded-lg border border-border bg-background-secondary px-3 py-2.5"
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <Typography size="sm" weight="semibold">
          {label}
        </Typography>
        <SegmentedControl
          aria-label={label}
          options={[OPERATOR, VIEWER].map(value => ({
            value,
            label: t(`share.roles.${value}.label`),
            disabled: isOperatorForced && value === VIEWER,
            tooltip:
              isOperatorForced && value === VIEWER
                ? t(`share.roles.${VIEWER}.ineligible`)
                : undefined
          }))}
          value={role}
          onValueChange={value => onRoleChange(value as TrustAccountMemberRole)}
        />
      </div>
      <Typography size="xs" color="tertiary" className="whitespace-normal">
        {t(`share.add.roleHints.${role}`)}
      </Typography>
      {children}
      {role === OPERATOR && hasGuests && (
        <Typography size="xs" color="tertiary" className="whitespace-normal">
          {t('share.add.guestNote')}
        </Typography>
      )}
    </div>
  );
};
